import { HttpErrorResponse, HttpEventType, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { from } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { ConnectivityService } from './connectivity.service';
import { environment } from '../../environments/environment';

/**
 * Attaches `Authorization: Bearer <access_token>` to API requests only, sourcing
 * the token from the active session (password OR Azure) via AuthService.
 * When `apiUrl` is an absolute base (prod), match requests that start with it.
 * When `apiUrl` is empty (dev, same-origin via the proxy), match same-origin
 * requests whose path starts with `/api`.
 *
 * It also reports reachability to ConnectivityService, which is what drains the offline
 * queues. A status-0 failure is the only proof available that the server is unreachable on a
 * network the browser still considers "online"; a successful response is the fastest proof it
 * came back. ConnectivityService probes with bare fetch(), never HttpClient, so there is no
 * cycle here — and drains fire on the false→true transition only, so the request a drain
 * makes cannot re-enter this path.
 *
 * While an Admin is acting as another user it also sends X-Act-As: <their id>.
 */
export const apiAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const apiUrl = environment.apiUrl;
  const isApiRequest = apiUrl ? req.url.startsWith(apiUrl) : req.url.startsWith('/api');
  if (!isApiRequest) {
    return next(req);
  }

  const auth = inject(AuthService);
  const connectivity = inject(ConnectivityService);
  return from(auth.getAccessToken()).pipe(
    switchMap((token) => {
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      // Read at send time, so a queued write replayed later still carries it while the Admin
      // is acting as someone (Exit is blocked until the queues drain).
      const target = auth.actingAs();
      if (target) headers['X-Act-As'] = String(target.id);
      return Object.keys(headers).length ? next(req.clone({ setHeaders: headers })) : next(req);
    }),
    tap({
      next: (ev) => { if (ev.type === HttpEventType.Response) connectivity.noteReachable(); },
      error: (err) => {
        if (err instanceof HttpErrorResponse && err.status === 0) connectivity.markUnreachable();
      },
    }),
  );
};
